import React, { useEffect, useState, useCallback } from 'react';
import { MdRefresh, MdExpandMore, MdExpandLess, MdClose } from 'react-icons/md';
import { tradingService } from '../../services/tradingService';
import API from '../../services/api';
import { useSortableRows, SortableTh } from '../../components/tables/SortableTable';
import LifecycleHistory, { EventSleevesModal } from './lifecycle/LifecycleHistory';

// Data-tab: Security Lifecycle review. The daily terminal beat runs the CIK discriminator and files
// a PENDING proposal (rename vs terminal) here; the human Confirms / Rejects. Detection RECOMMENDS,
// the human DECIDES. See apps/trading/services/terminal_classify.py + lifecycle_review.py and
// Plans/terminal_exclusion_from_calculations.md.

const STATUSES = ['pending', 'confirmed', 'rejected', 'applied', 'all'];

function ClassBadge({ cls, sameEntity }) {
  const rename = cls === 'rename';
  return (
    <span className={`px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wide ${
      rename ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
             : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'}`}>
      {rename ? 'Rename' : 'Terminal'}{sameEntity !== undefined ? (sameEntity ? ' · same issuer' : '') : ''}
    </span>
  );
}

// Format a numeric log/table value to 2-4dp; small magnitudes get more precision. Returns
// the raw string for non-numeric values so we never crash on an unexpected shape.
function fmtNum(v) {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  const decimals = Math.abs(n) !== 0 && Math.abs(n) < 1 ? 4 : 2;
  return n.toFixed(decimals);
}

const APPLY_LOG_NUMERIC_KEYS = new Set([
  'qty', 'quantity', 'basis', 'avg_entry_price', 'price', 'proceeds', 'realized_pnl',
  'before', 'after', 'successor_qty', 'successor_basis', 'ratio',
]);

function ApplyLogEntry({ entry }) {
  const { step, ...rest } = entry || {};
  const keys = Object.keys(rest).filter(k => rest[k] !== null && rest[k] !== undefined && rest[k] !== '');
  return (
    <div className="text-[12px] leading-relaxed">
      <span className="font-medium text-gray-800 dark:text-gray-200">{String(step || 'step').replace(/_/g, ' ')}</span>
      {keys.map(k => {
        const raw = rest[k];
        const isBool = typeof raw === 'boolean';
        const isNum = typeof raw === 'number' || (APPLY_LOG_NUMERIC_KEYS.has(k) && raw !== '' && !Number.isNaN(Number(raw)));
        const display = isBool ? String(raw) : (isNum ? fmtNum(raw) : String(raw));
        const isPnl = k === 'realized_pnl' && isNum;
        return (
          <span key={k} className="ml-2 text-gray-500 dark:text-gray-400">
            {k.replace(/_/g, ' ')}=<span className={isPnl ? (Number(raw) >= 0 ? 'text-green-600' : 'text-red-600') : ''}>{display}</span>
          </span>
        );
      })}
    </div>
  );
}

function AppliedRow({ r, expanded, onToggle, onReverse, onSleeves, busy }) {
  const hasLog = Array.isArray(r.apply_log) && r.apply_log.length > 0;
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wide bg-[#c9a96e]/15 text-[#c9a96e] border border-[#c9a96e]/40">
          Applied
        </span>
        <span className="text-[12px] text-gray-500">
          {r.applied_at ? String(r.applied_at).slice(0, 19).replace('T', ' ') : ''}
          {r.applied_by_username ? ` by ${r.applied_by_username}` : ''}
        </span>
        {onSleeves && (
          <button disabled={busy} onClick={onSleeves} title="Reopen the panel listing the sleeves this apply left with no symbol, with a per-sleeve Deactivate"
            className="px-2 py-0.5 rounded border border-[#c9a96e]/60 text-[#c9a96e] text-[11px] hover:bg-[#c9a96e]/10 disabled:opacity-50">
            Deactivate emptied sleeves…
          </button>
        )}
        {onReverse && (
          <button disabled={busy} onClick={onReverse} title="Undo this apply with mirror bookings (positions, reserves, symbol lists and the CA price restored); the card returns to Confirmed so it can be re-applied with corrected terms"
            className="px-2 py-0.5 rounded border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 text-[11px] hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50">
            Reverse…
          </button>
        )}
        {hasLog && (
          <button onClick={onToggle} className="ml-auto flex items-center gap-0.5 text-[11px] text-gray-500 hover:text-[#c9a96e]">
            {expanded ? <MdExpandLess size={14} /> : <MdExpandMore size={14} />} Apply log
          </button>
        )}
      </div>
      {expanded && hasLog && (
        <div className="mt-2 space-y-1.5 pl-2 border-l-2 border-gray-200 dark:border-gray-700">
          {r.apply_log.map((entry, i) => <ApplyLogEntry key={i} entry={entry} />)}
        </div>
      )}
    </div>
  );
}

function ApplyModal({
  review, plan, loading, err, priceInput, setPriceInput, ratioInput, setRatioInput,
  dateInput, setDateInput, onPriceBlur, onRatioBlur, onDateBlur, applying, onApply, onClose,
  priceAck, setPriceAck,
}) {
  const kind = plan?.kind;
  // A cash-out is only as good as its price. The deal consideration is NOT on any vendor tape, so the
  // field starts EMPTY, the last close is shown as a reference only, and Apply stays disabled until a
  // price is typed AND acknowledged as the per-share consideration (filing / broker credit).
  const priceReady = kind !== 'cash' || (priceInput !== '' && Number(priceInput) > 0 && priceAck);
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex sm:items-center sm:justify-center">
      <div className="w-full h-full sm:h-auto sm:max-w-lg sm:rounded-lg bg-white dark:bg-gray-900 sm:border sm:border-gray-200 dark:sm:border-gray-700 flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <div>
            <div className="font-serif text-lg text-gray-900 dark:text-gray-100">
              {review?.old_ticker}{plan?.successor ? ` → ${plan.successor}` : ''}
            </div>
            <div className="text-[11px] uppercase tracking-wide text-gray-500">{plan?.action_type || 'Apply disposition'}</div>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200" title="Close">
            <MdClose size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto scrollbar-thin px-4 py-3 space-y-4">
          {loading && !plan && <div className="py-8 text-center text-[13px] text-gray-400">Loading preview…</div>}
          {err && <div className="text-[13px] text-red-600 bg-red-50 dark:bg-red-900/20 rounded p-2">{err}</div>}

          {plan && !plan.writable && plan.block_reason && (
            <div className="text-[13px] text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded p-2">
              <span className="font-semibold uppercase text-[11px] tracking-wide block mb-0.5">Blocked</span>
              {plan.block_reason}
            </div>
          )}

          {plan && (
            <label className="flex flex-col text-[11px] text-gray-500 max-w-[200px]">
              Effective date
              <input type="date" value={dateInput}
                onChange={e => setDateInput(e.target.value)} onBlur={onDateBlur}
                className="mt-0.5 px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm" />
              <span className="mt-0.5 text-[10px] text-gray-400 normal-case">the fill is dated this day's close (NAV/TWR re-marked from here)</span>
            </label>
          )}

          {plan && kind === 'cash' && (
            <div className="space-y-1.5">
              <label className="flex flex-col text-[11px] text-gray-500 max-w-[220px]">
                Per-share cash consideration
                <input type="number" step="0.0001" value={priceInput} placeholder="type the deal price"
                  onChange={e => { setPriceInput(e.target.value); setPriceAck(false); }} onBlur={onPriceBlur}
                  className="mt-0.5 px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm" />
                <span className="mt-0.5 text-[10px] text-gray-400 normal-case">
                  {plan.reference_last_close != null
                    ? `last traded close ${Number(plan.reference_last_close).toFixed(2)} — reference only, NOT the deal terms`
                    : 'no last close on the tape'}
                  {plan.price_source === 'ca_cash_terminal' ? ' · pre-filled from the confirmed CA terms' : ''}
                </span>
              </label>
              <label className="flex items-start gap-2 text-[12px] text-gray-700 dark:text-gray-200">
                <input type="checkbox" checked={priceAck} onChange={e => setPriceAck(e.target.checked)} className="mt-0.5" />
                <span>I verified this is the per-share consideration from the SEC filing or the broker's cash credit (not the last close).</span>
              </label>
            </div>
          )}

          {plan && kind === 'swap' && (
            <label className="flex flex-col text-[11px] text-gray-500 max-w-[220px]">
              Ratio
              <input type="number" step="0.000001" value={ratioInput}
                onChange={e => setRatioInput(e.target.value)} onBlur={onRatioBlur}
                className="mt-0.5 px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm" />
              <span className="mt-0.5 text-[10px] text-gray-400 normal-case">successor shares per 1 old share</span>
            </label>
          )}

          {plan && Array.isArray(plan.positions) && plan.positions.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">Positions</div>
              <div className="overflow-x-auto scrollbar-thin">
                <table className="w-full text-[12px]">
                  <thead>
                    <tr className="text-left text-gray-400">
                      <th className="pr-2 py-1">Strategy</th>
                      <th className="pr-2 py-1">Account</th>
                      <th className="pr-2 py-1 text-right">Qty</th>
                      <th className="pr-2 py-1 text-right">Basis</th>
                      {kind === 'cash' && <th className="pr-2 py-1 text-right">Proceeds</th>}
                      {kind === 'cash' && <th className="pr-2 py-1 text-right">Realized P&amp;L</th>}
                      {kind === 'swap' && <th className="pr-2 py-1 text-right">Succ. Qty</th>}
                      {kind === 'swap' && <th className="pr-2 py-1 text-right">Succ. Basis</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {plan.positions.map((p, i) => (
                      <tr key={i} className="border-t border-gray-100 dark:border-gray-800 text-gray-700 dark:text-gray-300">
                        <td className="pr-2 py-1">{p.strategy}</td>
                        <td className="pr-2 py-1">{p.account_id}</td>
                        <td className="pr-2 py-1 text-right tabular-nums">{fmtNum(p.quantity)}</td>
                        <td className="pr-2 py-1 text-right tabular-nums">{fmtNum(p.avg_entry_price)}</td>
                        {kind === 'cash' && <td className="pr-2 py-1 text-right tabular-nums">{fmtNum(p.proceeds)}</td>}
                        {kind === 'cash' && (
                          <td className={`pr-2 py-1 text-right tabular-nums ${Number(p.realized_pnl) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {fmtNum(p.realized_pnl)}
                          </td>
                        )}
                        {kind === 'swap' && <td className="pr-2 py-1 text-right tabular-nums">{fmtNum(p.successor_qty)}</td>}
                        {kind === 'swap' && <td className="pr-2 py-1 text-right tabular-nums">{fmtNum(p.successor_basis)}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {plan && Array.isArray(plan.symbol_cleanup) && plan.symbol_cleanup.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">Symbol cleanup</div>
              <div className="space-y-1">
                {plan.symbol_cleanup.map((c, i) => (
                  <div key={i} className={`text-[12px] px-2 py-1 rounded ${c.only_symbol
                    ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800'
                    : 'text-gray-600 dark:text-gray-400'}`}>
                    <span className="font-medium">{c.name}</span>{c.only_symbol ? ' · only symbol' : ''} — {c.note}
                  </div>
                ))}
              </div>
            </div>
          )}

          {plan && Array.isArray(plan.warnings) && plan.warnings.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">Warnings</div>
              <ul className="text-[12px] text-amber-700 dark:text-amber-400 list-disc list-inside space-y-0.5">
                {plan.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}
        </div>

        <div className="flex gap-2 px-4 py-3 border-t border-gray-200 dark:border-gray-700">
          <button onClick={onClose}
            className="px-3 py-1.5 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 text-[13px] hover:bg-gray-50 dark:hover:bg-gray-800">
            Cancel
          </button>
          <button disabled={!plan?.writable || !priceReady || applying || loading} onClick={onApply}
            className="ml-auto px-3 py-1.5 rounded bg-green-600 text-white text-[13px] font-medium hover:opacity-90 disabled:opacity-50">
            {applying ? 'Applying…' : 'Confirm & Apply'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Wish #3 (2026-10-11): one previewed, human-clicked rename apply — spine alias (RENAME / MOVE / NOOP), the raw
// gap bars under the new ticker, the legacy Symbol heal + sleeve repoints — shown verbatim before anything runs.
function RenameModal({ review, plan, loading, err, dateInput, setDateInput, deactivateCa, setDeactivateCa, onRefetch, applying, onApply, onClose }) {
  const a = plan?.alias || {}; const g = plan?.gap || {}; const l = plan?.legacy || {}; const ca = plan?.terminal_ca;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex sm:items-center sm:justify-center">
      <div className="w-full h-full sm:h-auto sm:max-w-lg sm:rounded-lg bg-white dark:bg-gray-900 sm:border sm:border-gray-200 dark:sm:border-gray-700 flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <div>
            <div className="font-serif text-lg text-gray-900 dark:text-gray-100">{review?.old_ticker} → {review?.successor_ticker}</div>
            <div className="text-[11px] uppercase tracking-wide text-gray-500">Apply rename</div>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200" title="Close"><MdClose size={20} /></button>
        </div>
        <div className="flex-1 overflow-y-auto scrollbar-thin px-4 py-3 space-y-3 text-[13px]">
          {loading && !plan && <div className="py-8 text-center text-gray-400">Loading preview…</div>}
          {err && <div className="text-red-600 bg-red-50 dark:bg-red-900/20 rounded p-2">{err}</div>}
          {plan && !plan.writable && plan.block_reason && (
            <div className="text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded p-2">
              <span className="font-semibold uppercase text-[11px] tracking-wide block mb-0.5">Blocked</span>{plan.block_reason}
            </div>
          )}
          <label className="flex flex-col text-[11px] text-gray-500">Rename date (first session under the new ticker)
            <input type="date" value={dateInput} onChange={e => setDateInput(e.target.value)} onBlur={onRefetch}
              className="mt-0.5 px-2 py-1 w-44 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm" />
          </label>
          {ca && (
            <label className="flex items-start gap-2 p-2 rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20">
              <input type="checkbox" checked={deactivateCa} onChange={e => { setDeactivateCa(e.target.checked); setTimeout(onRefetch, 0); }} className="mt-0.5" />
              <span>An ACTIVE terminal record ({ca.action_type}, ex {ca.ex_date}) sits on this security. A rename is not a terminal event — tick to deactivate it (reversible, noted). Otherwise the alias step is blocked.</span>
            </label>
          )}
          {plan && (
            <div className="space-y-2">
              <div className="p-2 rounded border border-gray-200 dark:border-gray-700">
                <div className="text-[11px] uppercase tracking-wide text-gray-500">1 · Spine alias</div>
                <div><span className="font-mono">{a.action}</span> — {a.reason}</div>
                {a.action !== 'NOOP' && <div className="text-gray-500">close {review?.old_ticker} at {a.close_end_date}; open {review?.successor_ticker} from {a.open_start_date}{a.move_from ? ` (moving the boundary from ${a.move_from})` : ''}</div>}
                {(a.warnings || []).map((w, i) => <div key={i} className="text-amber-700 dark:text-amber-300">{w}</div>)}
              </div>
              <div className="p-2 rounded border border-gray-200 dark:border-gray-700">
                <div className="text-[11px] uppercase tracking-wide text-gray-500">2 · Raw bars</div>
                <div>last raw bar {g.last_raw_bar || '—'}; vendor bars to add under {review?.successor_ticker} from {g.from}: <span className="font-mono">{g.bars == null ? '?' : g.bars}</span></div>
              </div>
              <div className="p-2 rounded border border-gray-200 dark:border-gray-700">
                <div className="text-[11px] uppercase tracking-wide text-gray-500">3 · Legacy symbol table</div>
                <div><span className="font-mono">{l.action}</span> — {l.reason}</div>
                <div className="text-gray-500">old row {l.old_symbol ? `#${l.old_symbol.id} (${l.old_symbol.bars} bars)` : 'none'}; new row {l.new_symbol ? `#${l.new_symbol.id} (${l.new_symbol.bars} bars)` : 'none'}; colliding bars dropped {l.dailyprice_conflicts}; sleeves repointed {(l.strategy_repoints || []).length}</div>
              </div>
              {(plan.warnings || []).map((w, i) => <div key={i} className="text-amber-700 dark:text-amber-300">{w}</div>)}
            </div>
          )}
        </div>
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded border border-gray-300 dark:border-gray-600 text-[13px] text-gray-700 dark:text-gray-200">Cancel</button>
          <button disabled={!plan?.writable || applying || loading} onClick={onApply}
            className="px-3 py-1.5 rounded bg-[#c9a96e] text-white text-[13px] font-medium hover:opacity-90 disabled:opacity-50">
            {applying ? 'Applying…' : 'Confirm & Apply rename'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Wish #3: the daily spine-wide stale-raw-bars scan — what stopped, why, and what the detector made of it.
function StaleBarsPanel() {
  const [scan, setScan] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const load = useCallback(async () => {
    try { const { data } = await tradingService.lifecycleStaleBars(); setScan(data?.scan || null); } catch (e) { setScan(null); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const rescan = async () => {
    setBusy(true); setMsg('');
    try { const { data } = await tradingService.lifecycleRescan(false); setScan(data); setMsg('Fresh dry-run scan (cards not written).'); }
    catch (e) { setMsg(e?.response?.data?.error || 'Scan failed.'); } finally { setBusy(false); }
  };
  const rows = scan?.rows || [];
  const cols = [
    { key: 'class', type: 'string', get: r => r.class }, { key: 'ticker', type: 'string', get: r => r.ticker },
    { key: 'last_bar', type: 'string', get: r => r.last_bar || '' }, { key: 'sessions_stale', type: 'number', get: r => r.sessions_stale ?? -1 },
    { key: 'successor', type: 'string', get: r => r.successor || '' }, { key: 'held', type: 'number', get: r => (r.held ? 1 : 0) },
  ];
  const { sorted, toggle, indicator } = useSortableRows(rows, cols);
  return (
    <div className="mb-5 rounded-lg border border-gray-200 dark:border-gray-700">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between px-3 py-2 text-left">
        <span className="text-[11px] uppercase tracking-wide text-gray-500">Stale raw bars {scan ? `· ${scan.candidates} candidate(s) as of ${scan.as_of} (${scan.rename} rename · ${scan.terminal} terminal · ${scan.unknown} unknown)` : '· no scan yet'}</span>
        {open ? <MdExpandLess size={16} /> : <MdExpandMore size={16} />}
      </button>
      {open && (
        <div className="px-3 pb-3">
          <div className="flex items-center gap-2 mb-2 text-[12px] text-gray-500">
            <span>Spine-wide: a security is listed when its raw bars are stale by ≥ {scan?.n_sessions ?? 5} sessions or a vendor corporate-action feed flags its ticker.</span>
            <button disabled={busy} onClick={rescan} className="ml-auto px-2 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 disabled:opacity-50">{busy ? 'Scanning…' : 'Rescan (dry run)'}</button>
          </div>
          {msg && <div className="text-[12px] text-gray-600 dark:text-gray-300 mb-2">{msg}</div>}
          <div className="overflow-x-auto scrollbar-thin">
            <table className="min-w-full text-[12px]">
              <thead className="text-[10px] uppercase tracking-wider text-gray-500">
                <tr>
                  <SortableTh label="Class" colKey="class" toggle={toggle} indicator={indicator} />
                  <SortableTh label="Ticker" colKey="ticker" toggle={toggle} indicator={indicator} />
                  <SortableTh label="Last bar" colKey="last_bar" toggle={toggle} indicator={indicator} />
                  <SortableTh label="Stale" colKey="sessions_stale" toggle={toggle} indicator={indicator} />
                  <SortableTh label="Successor" colKey="successor" toggle={toggle} indicator={indicator} />
                  <SortableTh label="Held" colKey="held" toggle={toggle} indicator={indicator} />
                  <th className="px-2 py-1 text-left">Evidence</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(r => (
                  <tr key={r.ticker} className="border-t border-gray-100 dark:border-gray-800">
                    <td className="px-2 py-1"><span className={`px-1.5 py-0.5 rounded text-[10px] uppercase ${r.class === 'rename' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' : r.class === 'terminal' ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'}`}>{r.class}</span></td>
                    <td className="px-2 py-1 font-semibold tabular-nums">{r.ticker}</td>
                    <td className="px-2 py-1 tabular-nums">{r.last_bar || '—'}</td>
                    <td className="px-2 py-1 tabular-nums">{r.sessions_stale ?? '—'}</td>
                    <td className="px-2 py-1">{r.successor || '—'}{r.rename_date ? ` (${r.rename_date})` : ''}</td>
                    <td className="px-2 py-1">{r.held ? 'yes' : ''}{r.active_strategy ? ' · active' : ''}</td>
                    <td className="px-2 py-1 text-gray-500">{r.evidence}{r.review_id ? ` · review #${r.review_id}` : ''}</td>
                  </tr>
                ))}
                {!rows.length && <tr><td colSpan={7} className="px-2 py-4 text-center text-gray-400">Nothing stale or flagged.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SecurityLifecyclePage() {
  const [tab, setTab] = useState('review');                  // 'review' | 'history'
  const [sleevesFor, setSleevesFor] = useState(null);        // {id, title} — the per-event sleeves panel
  const [status, setStatus] = useState('pending');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [report, setReport] = useState({ ticker: '', successor: '', event_date: '' });
  const [reportMsg, setReportMsg] = useState('');

  // Confirm & Apply disposition flow (terminal names only — cash-out or swap-in a successor).
  const [modalReview, setModalReview] = useState(null);
  const [plan, setPlan] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [modalErr, setModalErr] = useState('');
  const [priceInput, setPriceInput] = useState('');
  const [ratioInput, setRatioInput] = useState('');
  const [dateInput, setDateInput] = useState('');
  const [priceAck, setPriceAck] = useState(false);
  const [applying, setApplying] = useState(false);
  const [expandedLog, setExpandedLog] = useState(() => new Set());
  // Wish #3: rename apply modal state
  const [renameReview, setRenameReview] = useState(null);
  const [renamePlan, setRenamePlan] = useState(null);
  const [renameLoading, setRenameLoading] = useState(false);
  const [renameErr, setRenameErr] = useState('');
  const [renameDate, setRenameDate] = useState('');
  const [deactivateCa, setDeactivateCa] = useState(false);
  const [renameApplying, setRenameApplying] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      // every page (the API pages at 50) — a status used to show only its first 50 cards
      const all = [];
      for (let page = 1; page <= 40; page += 1) {
        const { data } = await tradingService.listLifecycleReviewsPage(status, page);
        if (Array.isArray(data)) { all.push(...data); break; }
        all.push(...(data.results || []));
        if (!data.next) break;
      }
      setRows(all);
    } catch (e) {
      setErr('Failed to load reviews.');
    } finally { setLoading(false); }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const decide = async (id, action) => {
    let note = '';
    if (action === 'reject') {
      // The note is the audit record of WHY the proposal was wrong (e.g. "rename: same CIK, no holders").
      note = window.prompt('Reject — why is this proposal wrong? (recorded on the review)', '');
      if (note === null) return;
    }
    setBusyId(id);
    try {
      if (action === 'confirm') await tradingService.confirmLifecycleReview(id);
      else await tradingService.rejectLifecycleReview(id, note);
      await load();
    } catch (e) {
      setErr(`Action failed: ${e?.response?.data?.error || 'error'}`);
    } finally { setBusyId(null); }
  };

  // Re-fetch apply-preview for the open modal, optionally with an edited price/ratio. On the
  // initial call (no overrides) we seed the input fields from the server's defaults.
  const fetchPreview = async (id, { price, ratio, effectiveDate, initial } = {}) => {
    setPreviewLoading(true); setModalErr('');
    try {
      const payload = {};
      if (price !== undefined && price !== '') payload.price = price;
      if (ratio !== undefined && ratio !== '') payload.ratio = ratio;
      if (effectiveDate !== undefined && effectiveDate !== '') payload.effective_date = effectiveDate;
      const { data } = await API.post(`/api/trading/security-lifecycle/${id}/apply-preview/`, payload);
      setPlan(data);
      if (initial) {
        // price: seeded ONLY from confirmed CA terms, never from the tape (reference shown separately)
        setPriceInput(data?.price_source === 'ca_cash_terminal' && data?.price != null ? String(data.price) : '');
        setRatioInput(data?.ratio != null ? String(data.ratio) : '');
        setDateInput(data?.effective_date || '');
        setPriceAck(false);
      }
    } catch (e) {
      setModalErr(e?.response?.data?.error || 'Preview failed.');
      setPlan(null);
    } finally { setPreviewLoading(false); }
  };

  const openApplyFlow = async (r) => {
    setBusyId(r.id);
    setErr('');
    try {
      if (r.status === 'pending') {
        await tradingService.confirmLifecycleReview(r.id);
        await load();
      }
      setModalReview(r);
      setPlan(null);
      setModalErr('');
      setPriceInput('');
      setRatioInput('');
      setDateInput('');
      setPriceAck(false);
      await fetchPreview(r.id, { initial: true });
    } catch (e) {
      setErr(`Action failed: ${e?.response?.data?.error || 'error'}`);
    } finally { setBusyId(null); }
  };

  // Undo an applied cash disposition (mirror bookings; never deletes). Confirmed twice: here and server-side.
  const reverseApply = async (r) => {
    const msg = `Reverse the ${r.old_ticker} apply?\n\nThis books mirror BUYs at the same price and date so positions, reserves and symbol lists return to their pre-apply state, clears the CA price, re-marks the same snapshots, and returns the card to Confirmed. The original apply stays in the log as history.`;
    if (!window.confirm(msg)) return;
    setBusyId(r.id); setErr('');
    try {
      await tradingService.reverseLifecycleReview(r.id);
      await load();
    } catch (e) {
      setErr(`Reverse failed: ${e?.response?.data?.error || 'error'}`);
    } finally { setBusyId(null); }
  };

  const closeModal = () => {
    setModalReview(null);
    setPlan(null);
    setModalErr('');
    setPriceInput('');
    setRatioInput('');
    setDateInput('');
    setPriceAck(false);
  };

  const refetch = () => { if (modalReview) fetchPreview(modalReview.id, { price: priceInput, ratio: ratioInput, effectiveDate: dateInput }); };
  const onPriceBlur = refetch;
  const onRatioBlur = refetch;
  const onDateBlur = refetch;

  const applyNow = async () => {
    if (!modalReview || !plan?.writable) return;
    setApplying(true); setModalErr('');
    try {
      const payload = { confirm: true };
      if (priceInput !== '') payload.price = priceInput;
      if (ratioInput !== '') payload.ratio = ratioInput;
      if (dateInput !== '') payload.effective_date = dateInput;
      await API.post(`/api/trading/security-lifecycle/${modalReview.id}/apply/`, payload);
      const reviewId = modalReview.id;
      const ticker = plan?.ticker;
      closeModal();
      await load();
      // the sleeves this event touched, with what each still holds and a Stop button (never automatic)
      setSleevesFor({ id: reviewId, title: ticker });
    } catch (e) {
      setModalErr(e?.response?.data?.error || 'Apply failed.');
    } finally { setApplying(false); }
  };

  // The sleeves panel for any terminal card (before or after an apply): current holdings and a Stop button.
  const openSleevesPanel = (r) => setSleevesFor({ id: r.id, title: r.old_ticker });

  const fetchRenamePreview = async (r, date, deact) => {
    setRenameLoading(true); setRenameErr('');
    try {
      const { data } = await tradingService.lifecycleRenamePreview(r.id, { rename_date: date || undefined, deactivate_terminal_ca: !!deact });
      setRenamePlan(data);
    } catch (e) { setRenameErr(e?.response?.data?.error || 'Preview failed.'); } finally { setRenameLoading(false); }
  };
  const openRenameFlow = (r) => {
    const d = r.event_date || '';
    setRenameReview(r); setRenamePlan(null); setRenameDate(d); setDeactivateCa(false); setRenameErr('');
    fetchRenamePreview(r, d, false);
  };
  const applyRename = async () => {
    if (!renameReview) return;
    setRenameApplying(true); setRenameErr('');
    try {
      await tradingService.lifecycleRenameApply(renameReview.id, { rename_date: renameDate || undefined, deactivate_terminal_ca: deactivateCa });
      setRenameReview(null); setRenamePlan(null);
      await load();
    } catch (e) { setRenameErr(e?.response?.data?.error || 'Apply failed.'); } finally { setRenameApplying(false); }
  };

  const toggleLog = (id) => {
    setExpandedLog(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const submitReport = async (e) => {
    e.preventDefault();
    setReportMsg('');
    try {
      const payload = { ticker: report.ticker.trim().toUpperCase() };
      if (report.successor.trim()) payload.successor = report.successor.trim().toUpperCase();
      if (report.event_date) payload.event_date = report.event_date;
      const { data } = await tradingService.reportLifecycleReview(payload);
      const cls = data?.proposal?.class;
      setReportMsg(cls === 'rename'
        ? `Filed — discriminator flags this as a RENAME (same issuer). Review it below.`
        : `Filed as ${cls || 'terminal'}. Review it below.`);
      setReport({ ticker: '', successor: '', event_date: '' });
      if (status !== 'pending') setStatus('pending'); else load();
    } catch (e) {
      setReportMsg(e?.response?.data?.error || 'Report failed.');
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-3 sm:px-4 py-4">
      <div className="flex items-center justify-between mb-1">
        <h1 className="font-serif text-xl text-gray-900 dark:text-gray-100">Security Lifecycle</h1>
        {tab === 'review' && (
          <button onClick={load} className="p-2 text-gray-500 hover:text-[#c9a96e]" title="Refresh">
            <MdRefresh className={loading ? 'animate-spin' : ''} size={20} />
          </button>
        )}
      </div>
      <div className="flex gap-4 border-b border-gray-200 dark:border-gray-700 mb-4">
        {[['review', 'Review'], ['history', 'History']].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`pb-2 text-[13px] uppercase tracking-wide border-b-2 -mb-px ${tab === k
              ? 'border-[#c9a96e] text-gray-900 dark:text-gray-100' : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}>
            {label}
          </button>
        ))}
      </div>
      {tab === 'history' ? <LifecycleHistory /> : (<>
      <p className="text-[13px] text-gray-500 dark:text-gray-400 mb-4">
        Detected renames &amp; terminal events awaiting your decision. The system recommends (CIK
        discriminator); you confirm or reject. A rename continues the series under a new ticker; a
        terminal halts it. Nothing auto-liquidates or auto-trades. A confirmed rename is applied from its card (previewed: alias, gap bars, legacy heal).
      </p>

      {/* Report form (reverse direction) */}
      <form onSubmit={submitReport} className="mb-5 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40">
        <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-2">Report a lifecycle event</div>
        <div className="flex flex-wrap gap-2 items-end">
          <label className="flex flex-col text-[11px] text-gray-500">Ticker
            <input required value={report.ticker} onChange={e => setReport({ ...report, ticker: e.target.value })}
              className="mt-0.5 px-2 py-1 w-28 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm" placeholder="AVB" />
          </label>
          <label className="flex flex-col text-[11px] text-gray-500">Successor (optional)
            <input value={report.successor} onChange={e => setReport({ ...report, successor: e.target.value })}
              className="mt-0.5 px-2 py-1 w-28 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm" placeholder="VMRK" />
          </label>
          <label className="flex flex-col text-[11px] text-gray-500">Event date (optional)
            <input type="date" value={report.event_date} onChange={e => setReport({ ...report, event_date: e.target.value })}
              className="mt-0.5 px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm" />
          </label>
          <button type="submit" className="px-3 py-1.5 rounded bg-[#c9a96e] text-white text-sm font-medium hover:opacity-90">Check &amp; file</button>
        </div>
        {reportMsg && <div className="mt-2 text-[12px] text-gray-600 dark:text-gray-300">{reportMsg}</div>}
      </form>

      <StaleBarsPanel />

      {/* Status filter */}
      <div className="flex gap-1 mb-3">
        {STATUSES.map(s => (
          <button key={s} onClick={() => setStatus(s)}
            className={`px-2.5 py-1 rounded text-[12px] capitalize ${status === s
              ? 'bg-[#c9a96e] text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'}`}>
            {s}
          </button>
        ))}
      </div>

      {err && <div className="mb-3 text-[13px] text-red-600">{err}</div>}
      {!loading && rows.length === 0 && (
        <div className="py-8 text-center text-[13px] text-gray-400">No {status === 'all' ? '' : `${status} `}reviews.</div>
      )}

      <div className="space-y-2">
        {rows.map(r => (
          <div key={r.id} className="p-3 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <ClassBadge cls={r.proposed_class} sameEntity={r.same_entity} />
              <span className="font-semibold text-gray-900 dark:text-gray-100 tabular-nums">
                {r.old_ticker || '—'}{r.successor_ticker ? ` → ${r.successor_ticker}` : ''}
              </span>
              <span className="text-[12px] text-gray-500">{r.security_name}</span>
              {r.event_date && <span className="text-[11px] text-gray-400 tabular-nums">ex {r.event_date}</span>}
              {r.ratio != null && (
                <span className="text-[11px] text-gray-500">ratio {Number(r.ratio).toFixed(4)}
                  <span className={`ml-1 ${r.corroborated ? 'text-green-600' : 'text-amber-600'}`}>
                    ({r.corroborated ? 'corroborated' : r.ratio_source})
                  </span>
                </span>
              )}
              <span className="ml-auto text-[11px] text-gray-400">{r.source === 'human_report' ? 'reported' : 'auto'}</span>
            </div>
            {r.evidence && <div className="text-[12px] text-gray-600 dark:text-gray-400 mb-2">{r.evidence}</div>}
            {r.proposed_class === 'terminal' && r.status !== 'rejected' && (
              <button onClick={() => openSleevesPanel(r)}
                className="mb-2 text-[12px] text-[#c9a96e] hover:underline">Sleeves on {r.old_ticker || 'this stock'} — holdings &amp; Stop…</button>
            )}
            {r.status === 'pending' ? (
              <div className="flex gap-2">
                <button disabled={busyId === r.id}
                  onClick={() => (r.proposed_class === 'terminal' ? openApplyFlow(r) : decide(r.id, 'confirm'))}
                  className="px-3 py-1 rounded bg-green-600 text-white text-[13px] font-medium hover:opacity-90 disabled:opacity-50">
                  {r.proposed_class === 'terminal' ? 'Confirm & Apply…' : 'Confirm'}
                </button>
                <button disabled={busyId === r.id} onClick={() => decide(r.id, 'reject')}
                  className="px-3 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 text-[13px] hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50">
                  Reject
                </button>
              </div>
            ) : r.status === 'confirmed' && r.proposed_class === 'rename' ? (
              <div className="flex flex-wrap items-center gap-2">
                <button disabled={busyId === r.id} onClick={() => openRenameFlow(r)}
                  className="px-3 py-1 rounded bg-[#c9a96e] text-white text-[13px] font-medium hover:opacity-90 disabled:opacity-50">
                  Preview &amp; Apply rename…
                </button>
                <button disabled={busyId === r.id} onClick={() => decide(r.id, 'reject')}
                  className="px-3 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 text-[13px] hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50">
                  Reject
                </button>
                <span className="text-[12px] text-gray-500">
                  confirmed {r.decided_by_username ? `by ${r.decided_by_username}` : ''} {r.decided_at ? `· ${String(r.decided_at).slice(0, 10)}` : ''}
                </span>
              </div>
            ) : r.status === 'confirmed' && r.proposed_class === 'terminal' ? (
              <div className="flex flex-wrap items-center gap-2">
                <button disabled={busyId === r.id} onClick={() => openApplyFlow(r)}
                  className="px-3 py-1 rounded bg-[#c9a96e] text-white text-[13px] font-medium hover:opacity-90 disabled:opacity-50">
                  Apply…
                </button>
                {/* A confirmed-but-never-applied review can still be rejected (e.g. it turned out to be a rename). */}
                <button disabled={busyId === r.id} onClick={() => decide(r.id, 'reject')}
                  className="px-3 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 text-[13px] hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50">
                  Reject
                </button>
                <span className="text-[12px] text-gray-500">
                  confirmed {r.decided_by_username ? `by ${r.decided_by_username}` : ''} {r.decided_at ? `· ${String(r.decided_at).slice(0, 10)}` : ''}
                </span>
              </div>
            ) : r.status === 'applied' ? (
              <AppliedRow r={r} expanded={expandedLog.has(r.id)} onToggle={() => toggleLog(r.id)}
                busy={busyId === r.id} onReverse={() => reverseApply(r)} />
            ) : (
              <div className="text-[12px] text-gray-500">
                {r.status} {r.decided_by_username ? `by ${r.decided_by_username}` : ''} {r.decided_at ? `· ${String(r.decided_at).slice(0, 10)}` : ''}
              </div>
            )}
          </div>
        ))}
      </div>

      </>)}

      {sleevesFor && (
        <EventSleevesModal reviewId={sleevesFor.id} title={sleevesFor.title}
          onClose={() => setSleevesFor(null)} onChanged={load} />
      )}

      {renameReview && (
        <RenameModal review={renameReview} plan={renamePlan} loading={renameLoading} err={renameErr}
          dateInput={renameDate} setDateInput={setRenameDate} deactivateCa={deactivateCa} setDeactivateCa={setDeactivateCa}
          onRefetch={() => fetchRenamePreview(renameReview, renameDate, deactivateCa)}
          applying={renameApplying} onApply={applyRename} onClose={() => { setRenameReview(null); setRenamePlan(null); }} />
      )}

      {modalReview && (
        <ApplyModal
          review={modalReview}
          plan={plan}
          loading={previewLoading}
          err={modalErr}
          priceInput={priceInput}
          setPriceInput={setPriceInput}
          ratioInput={ratioInput}
          setRatioInput={setRatioInput}
          dateInput={dateInput}
          setDateInput={setDateInput}
          onPriceBlur={onPriceBlur}
          onRatioBlur={onRatioBlur}
          onDateBlur={onDateBlur}
          priceAck={priceAck}
          setPriceAck={setPriceAck}
          applying={applying}
          onApply={applyNow}
          onClose={closeModal}
        />
      )}
    </div>
  );
}
